import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
  ContentLanguage,
  toContentLanguage,
} from 'app/api/models/user/content-language';
import { RestrictedContentLanguageOptions } from 'app/api/models/shared/contentLanguageOptions';
import {
  SalesProfileQuestionnaireDto,
  SalesProfileQuestionnaireLanguageDto,
} from 'app/api/models/salesProfile/salesProfileQuestionnaireDto';
import { SalesProfileQuestionnaireService } from 'app/api/services/sales-profile-questionnaire.service';
import {
  SALES_PROFILE_QUESTIONS,
  salesProfileTagLabel,
  SalesProfileQuestionStructure,
} from 'app/modules/core/components/sales-profile-questionnaire/sales-profile-content';
import { MessageText, ToastService } from 'app/helpers/services/toastService';

const STEP_COUNT = 3;

/**
 * The tenant's Questionnaire tab: the sales-profile questions the home page
 * asks, one set per content language. Each tenant owns its own copy.
 *
 * Only wording is editable. The structure — six questions, their option keys
 * and the methodology tag each option maps to — is fixed in the client, because
 * the tags are the keys the scoring, the focus-area descriptions and the sprint
 * matching all run on.
 */
@Component({
  selector: 'app-tenant-questionnaire',
  standalone: false,
  templateUrl: './tenant-questionnaire.component.html',
  styleUrls: ['./tenant-questionnaire.component.scss'],
})
export class TenantQuestionnaireComponent implements OnChanges {
  @Input() id: string | null = null;

  readonly structure = SALES_PROFILE_QUESTIONS;

  languages: SalesProfileQuestionnaireLanguageDto[] = [];
  language: ContentLanguage = ContentLanguage.Danish;

  isLoadingLanguages = true;
  isLoading = true;
  isSaving = false;
  isLoadingDefaults = false;
  /** False when this tenant has no stored copy for the selected language yet. */
  hasContent = false;

  /**
   * Unsaved edits per language. Switching languages refetches from the server,
   * which would otherwise throw away whatever Clear or Load Default put in the
   * form; a language is only cached while it has pending changes.
   */
  private readonly drafts = new Map<ContentLanguage, SalesProfileQuestionnaireDto>();

  constructor(
    private fb: FormBuilder,
    private questionnaire: SalesProfileQuestionnaireService,
    private toast: ToastService,
  ) {}

  form = this.fb.group({
    eyebrow: [''],
    heading: [''],
    introText: [''],
    duration: [''],
    cardTitle: [''],
    cardSubtitle: [''],
    questionCounterTemplate: [''],
    backLabel: [''],
    nextLabel: [''],
    finishLabel: [''],
    steps: this.fb.array<FormGroup>([]),
    questions: this.fb.array<FormGroup>([]),
  });

  get steps(): FormArray<FormGroup> {
    return this.form.get('steps') as FormArray<FormGroup>;
  }

  get questions(): FormArray<FormGroup> {
    return this.form.get('questions') as FormArray<FormGroup>;
  }

  options(question: FormGroup): FormArray<FormGroup> {
    return question.get('options') as FormArray<FormGroup>;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id'] && this.id) {
      this.loadLanguages();
    }
  }

  languageLabel(language: ContentLanguage): string {
    return (
      RestrictedContentLanguageOptions.find((o) => o.value === language)
        ?.label ?? ''
    );
  }

  selectLanguage(language: ContentLanguage): void {
    if (this.language === language) {
      return;
    }

    if (this.form.dirty) {
      this.drafts.set(this.language, this.toDto());
    }

    this.language = language;

    const draft = this.drafts.get(language);
    if (!draft) {
      this.load();
      return;
    }

    // Pending edits win over the stored copy; the badge and the banner keep
    // showing what is actually in the database.
    this.hasContent = this.storedFor(language);
    this.buildForm(draft);
    this.form.markAsDirty();
    this.isLoading = false;
  }

  private storedFor(language: ContentLanguage): boolean {
    return !!this.languages.find((x) => x.language === language)?.hasContent;
  }

  private loadLanguages(): void {
    this.isLoadingLanguages = true;
    this.drafts.clear();
    this.questionnaire.getLanguages(this.id!).subscribe({
      next: (languages) => {
        this.languages = languages;
        this.isLoadingLanguages = false;
        // Open on a language that already has questions when there is one, so
        // the tab does not land on an empty form for no reason.
        const filled = languages.find((x) => x.hasContent);
        this.language =
          toContentLanguage(filled?.language) ?? ContentLanguage.Danish;
        this.load();
      },
      error: () => {
        this.isLoadingLanguages = false;
        this.isLoading = false;
      },
    });
  }

  private load(): void {
    this.isLoading = true;
    this.questionnaire.getByLanguage(this.id!, this.language).subscribe({
      next: (content) => {
        this.hasContent = !!content;
        this.buildForm(content);
        this.form.markAsPristine();
        this.isLoading = false;
      },
      error: () => {
        this.hasContent = false;
        this.buildForm(null);
        this.form.markAsPristine();
        this.isLoading = false;
      },
    });
  }

  /**
   * Lays the form out from the fixed structure and fills it with whatever copy
   * is stored, so a language with no content yet still opens as a complete,
   * empty questionnaire rather than a blank page.
   */
  private buildForm(content: SalesProfileQuestionnaireDto | null): void {
    this.form.patchValue({
      eyebrow: content?.eyebrow ?? '',
      heading: content?.heading ?? '',
      introText: content?.introText ?? '',
      duration: content?.duration ?? '',
      cardTitle: content?.cardTitle ?? '',
      cardSubtitle: content?.cardSubtitle ?? '',
      questionCounterTemplate: content?.questionCounterTemplate ?? '',
      backLabel: content?.backLabel ?? '',
      nextLabel: content?.nextLabel ?? '',
      finishLabel: content?.finishLabel ?? '',
    });

    this.steps.clear();
    for (let order = 0; order < STEP_COUNT; order++) {
      const step = content?.steps.find((s) => s.order === order);
      this.steps.push(
        this.fb.group({
          order: [order],
          title: [step?.title ?? ''],
          sub: [step?.sub ?? ''],
        }),
      );
    }

    this.questions.clear();
    this.structure.forEach((structure) => {
      const question = content?.questions.find(
        (q) => q.number === structure.number,
      );
      this.questions.push(
        this.fb.group({
          number: [structure.number],
          title: [question?.title ?? ''],
          hint: [question?.hint ?? ''],
          note: [question?.note ?? ''],
          freeTextLabel: [question?.freeTextLabel ?? ''],
          freeTextPlaceholder: [question?.freeTextPlaceholder ?? ''],
          options: this.fb.array<FormGroup>(
            structure.options.map((option) =>
              this.fb.group({
                optionKey: [option.id],
                label: [
                  question?.options.find((o) => o.optionKey === option.id)
                    ?.label ?? '',
                ],
              }),
            ),
          ),
        }),
      );
    });
  }

  structureFor(number: number): SalesProfileQuestionStructure | undefined {
    return this.structure.find((s) => s.number === number);
  }

  /**
   * The methodology tag an option maps to, shown read-only so the admin can see
   * what an answer selects. Displayed in the language being edited; the stored
   * tag itself stays Danish.
   */
  tagFor(questionNumber: number, optionKey: string): string {
    const tag =
      this.structureFor(questionNumber)?.options.find(
        (o) => o.id === optionKey,
      )?.tag ?? '';
    return salesProfileTagLabel(tag, this.language);
  }

  hasFreeText(questionNumber: number): boolean {
    return !!this.structureFor(questionNumber)?.hasFreeText;
  }

  /**
   * Fills the form with the wording the app ships with. Nothing is written
   * until Save, so the stored copy — and the language badges that reflect it —
   * stay untouched.
   */
  loadDefaults(): void {
    if (this.isLoadingDefaults) {
      return;
    }
    this.isLoadingDefaults = true;
    this.questionnaire.getDefaults(this.language).subscribe({
      next: (defaults) => {
        if (defaults) {
          this.buildForm(defaults);
          this.form.markAsDirty();
        }
        this.isLoadingDefaults = false;
      },
      error: () => {
        this.isLoadingDefaults = false;
        this.toast.showError(MessageText.error);
      },
    });
  }

  /** Empties every field. Like Load Default, it only touches the form — the
   *  stored copy is untouched until Save. */
  clear(): void {
    this.buildForm(null);
    this.form.markAsDirty();
  }

  /**
   * True when every field is blank. Saving in that state means "this language
   * has no questions" and removes the stored copy, which is what Clear is for.
   */
  /** Whether the stored copy for the selected language is finished. */
  get isCurrentComplete(): boolean {
    return !!this.languages.find((x) => x.language === this.language)
      ?.isComplete;
  }

  get isFormEmpty(): boolean {
    return !this.textValues().some((value) => !!value.trim());
  }

  /** The form as it stands, in the shape the API and the draft cache both use. */
  private toDto(): SalesProfileQuestionnaireDto {
    const value = this.form.getRawValue();
    return {
      tenantId: this.id ?? '',
      language: this.language,
      eyebrow: value.eyebrow ?? '',
      heading: value.heading ?? '',
      introText: value.introText ?? '',
      duration: value.duration ?? '',
      cardTitle: value.cardTitle ?? '',
      cardSubtitle: value.cardSubtitle ?? '',
      questionCounterTemplate: value.questionCounterTemplate ?? '',
      backLabel: value.backLabel ?? '',
      nextLabel: value.nextLabel ?? '',
      finishLabel: value.finishLabel ?? '',
      steps: this.steps.controls.map((step) => ({
        order: step.get('order')?.value,
        title: step.get('title')?.value ?? '',
        sub: step.get('sub')?.value ?? '',
      })),
      questions: this.questions.controls.map((question) => ({
        number: question.get('number')?.value,
        title: question.get('title')?.value ?? '',
        hint: question.get('hint')?.value ?? '',
        note: question.get('note')?.value || null,
        freeTextLabel: question.get('freeTextLabel')?.value || null,
        freeTextPlaceholder: question.get('freeTextPlaceholder')?.value || null,
        options: this.options(question).controls.map((option) => ({
          optionKey: option.get('optionKey')?.value,
          label: option.get('label')?.value ?? '',
        })),
      })),
    };
  }

  private textValues(): string[] {
    const value = this.form.getRawValue();
    return [
      value.eyebrow ?? '',
      value.heading ?? '',
      value.introText ?? '',
      value.duration ?? '',
      value.cardTitle ?? '',
      value.cardSubtitle ?? '',
      value.questionCounterTemplate ?? '',
      value.backLabel ?? '',
      value.nextLabel ?? '',
      value.finishLabel ?? '',
      ...this.steps.controls.flatMap((step) => [
        step.get('title')?.value ?? '',
        step.get('sub')?.value ?? '',
      ]),
      ...this.questions.controls.flatMap((question) => [
        question.get('title')?.value ?? '',
        question.get('hint')?.value ?? '',
        question.get('note')?.value ?? '',
        question.get('freeTextLabel')?.value ?? '',
        question.get('freeTextPlaceholder')?.value ?? '',
        ...this.options(question).controls.map(
          (option) => option.get('label')?.value ?? '',
        ),
      ]),
    ];
  }

  save(): void {
    if (this.isSaving || !this.id) {
      return;
    }

    // An emptied form is a deletion, not an invalid save — the required
    // validators only guard against storing a half-filled questionnaire.
    if (this.isFormEmpty) {
      this.deleteStored();
      return;
    }

    // Saving is allowed at any stage; an unfinished copy simply is not served
    // to users until every field is filled in.
    this.isSaving = true;

    this.questionnaire.save(this.id, this.language, this.toDto()).subscribe({
      next: () => {
        this.markStored(true);
        this.isSaving = false;
        this.toast.showSuccess(MessageText.saved);
      },
      error: () => {
        this.isSaving = false;
        this.toast.showError(MessageText.error);
      },
    });
  }

  private deleteStored(): void {
    this.isSaving = true;
    this.questionnaire.delete(this.id!, this.language).subscribe({
      next: () => {
        this.markStored(false);
        this.isSaving = false;
        this.toast.showSuccess(MessageText.saved);
      },
      error: () => {
        this.isSaving = false;
        this.toast.showError(MessageText.error);
      },
    });
  }

  /**
   * Reflects what is now in the database: the banner, the language switcher
   * badges and the unsaved-changes marker, without refetching the list.
   */
  private markStored(stored: boolean): void {
    this.hasContent = stored;
    this.form.markAsPristine();
    // Now identical to the server, so the draft has nothing left to protect.
    this.drafts.delete(this.language);

    // Completeness is decided by the server, so the badges are refetched rather
    // than guessed at here — one small call, and no second copy of the rule.
    this.questionnaire.getLanguages(this.id!).subscribe({
      next: (languages) => (this.languages = languages),
    });
  }
}
